import { descriptorPropTypes } from "lib/common/descriptorcontrols/formhelpers";
import EntityProxy from "data/util/EntityProxy";
import entityMetaStore from "data/meta/entityMetaStore";
import React from "react";
import PropTypes from "prop-types";
import RecordGridDataGateway, {
    buildColumnsFromConfig,
    buildCreatable,
    buildDevicelesiongridCheckboxColumns,
} from "../RecordGridDataGateway";
import RecordActions, { OPT_USER_CONFIRMED } from "data/RecordActions";
import Dispatcher from "Dispatcher";

import Control
    from "lib/common/descriptorcontrols/CompositionField/devicelesiongrid";

/**
 * DeviceLesionGrid composition field component with enablement tracking.
 * 
 * This component tracks enablement state changes to handle the scenario where
 * a parent field's EnableScript dynamically disables the grid. Since React's
 * shallow comparison doesn't detect changes within the same EntityProxy reference,
 * we explicitly track the enabled state and clear grid items when transitioning
 * from enabled to disabled.
 * 
 * @see HJC-661 - Correct issue with devicelesiongrid enablement
 */
export class CompositionFielddevicelesiongrid extends React.Component {
    /**
     * Tracks enablement state to detect when the field's EnableScript result changes.
     * Since activeRecord is the same EntityProxy instance across renders, React's
     * shallow comparison won't detect enablement changes. We must explicitly track
     * the computed enabled state in component state.
     * 
     * @param {Object} props - Component props
     * @see HJC-661 - Fix for devicelesiongrid enablement tracking
     */
    constructor(props) {
        super(props);
        this.state = {
            wasEnabled: this.isEnabled(props)
        };
        this.clearGridItems = this.clearGridItems.bind(this);
    }

    /**
     * Computes the current enabled state by calling activeRecord.isFieldEnabled().
     * This evaluates the EnableScript and returns the current enabled state.
     * 
     * @param {Object} props - Component props (defaults to this.props)
     * @returns {boolean} True if field is enabled, false otherwise
     */
    isEnabled(props = this.props) {
        const { activeRecord, descriptor } = props;
        return activeRecord.isFieldEnabled(descriptor.key);
    }

    /**
     * Detects enablement state changes and clears grid when transitioning to disabled.
     * This ensures the UI reflects the business logic: when a field becomes disabled,
     * its value should be cleared (empty device list).
     * 
     * @param {Object} prevProps - Previous component props
     */
    componentDidUpdate(prevProps) {
        const currentlyEnabled = this.isEnabled();
        const { wasEnabled } = this.state;
        
        // Detect enablement state change
        if (wasEnabled !== currentlyEnabled) {
            this.setState({ wasEnabled: currentlyEnabled });
            
            // If transitioning from enabled to disabled, clear the grid.
            // Pass prevProps.activeRecord so clearGridItems can read records that existed
            // BEFORE the TableStore enablement system cleared the composition collection.
            if (!currentlyEnabled && wasEnabled) {
                this.clearGridItems(prevProps.activeRecord);
            }
        }
    }

    /**
     * Clears all grid items by dispatching DELETE_CHILD actions.
     *
     * The TableStore enablement system automatically clears the composition collection
     * when the field becomes disabled, before React's componentDidUpdate fires.
     * We read records from prevActiveRecord (the pre-disable state) to know what to delete.
     *
     * @param {EntityProxy} prevActiveRecord - activeRecord from prevProps, holds old record list
     */
    clearGridItems(prevActiveRecord) {
        const { activeRecord, descriptor, fluxContext } = this.props;
        const config = descriptor.config;
        
        // Defensive check for config
        if (!config || !config.rowSourceEntityFieldKey) {
            console.warn("devicelesiongrid: missing config for grid clearing");
            return;
        }

        // Read from prevActiveRecord — current state was already cleared by enablement system
        const prevRecord = prevActiveRecord || activeRecord;
        const records = prevRecord.get(config.rowSourceEntityFieldKey) || [];
        
        // Dispatch DELETE_CHILD action for each record to maintain data integrity
        records.forEach(childRecord => {
            Dispatcher.contextDispatch(fluxContext, {
                type: RecordActions.DELETE_CHILD,
                recordKey: activeRecord.entityKey,
                recordId: activeRecord.id,
                fieldKey: config.rowSourceEntityFieldKey,
                childKey: childRecord.entityKey,
                childId: childRecord.id,
                [OPT_USER_CONFIRMED]: true,
            });
        });
    }

    render() {
        const {
            descriptor,
            fluxContext,
            activeRecord,
            onFocus,
            onBlur,
            onKeyDown,
        } = this.props;
        const config = descriptor.config;

        const creatables = [];
        for (let i = 0, l = config.creatables.length; i < l; i++) {
            const creatable = config.creatables[i];
            const ei = entityMetaStore.getInfo(creatable.key);
            creatables.push(buildCreatable(ei.implKey, creatable.name, creatable.key, creatable.collectionKey, activeRecord));
        }

        const columns = buildColumnsFromConfig(config.columns, this.props)
            .concat(
                buildDevicelesiongridCheckboxColumns(
                    this.props, null
                )
            );
        return (
            <RecordGridDataGateway
                key={descriptor.key}
                enabled={activeRecord.isFieldEnabled(descriptor.key)}
                component={Control}
                activeRecord={activeRecord}
                shortname={config.rowSourceEntityFieldKey}
                singularLabel={config.otherEntityName}
                fluxContext={fluxContext}
                descriptor={descriptor}
                addMode={config.addMode}
                allowView={!config.inline}
                allowCreate={true}
                allowRemove={true}
                columns={columns}
                creatables={creatables}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
            />
        );
    }
}

CompositionFielddevicelesiongrid.propTypes = {
    descriptor: PropTypes.shape(descriptorPropTypes({
        inline: PropTypes.bool,
        otherEntityName: PropTypes.string.isRequired,
        rowSourceEntityFieldKey: PropTypes.string.isRequired,
        rowSourceEntityCollectionKey: PropTypes.string.isRequired,
        addMode: PropTypes.string.isRequired,
        columns: PropTypes.arrayOf(PropTypes.shape({
            heading: PropTypes.string.isRequired,
            width: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]).isRequired,
            type: PropTypes.string.isRequired,
            script: PropTypes.string,
            itemInfo: PropTypes.object,
        })).isRequired,
        creatables: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string.isRequired,
            key: PropTypes.string.isRequired,
            collectionKey: PropTypes.string.isRequired,
        })).isRequired,
    })),
    fluxContext: PropTypes.string.isRequired,
    activeRecord: PropTypes.instanceOf(EntityProxy).isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
};

export default CompositionFielddevicelesiongrid;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/formitems/CompositionField/devicelesiongrid.js